import { Button } from "@promptwatch/ui/components/button";
import { Card, CardContent } from "@promptwatch/ui/components/card";
import { ArrowRight, GraduationCap, HandCoins, Layers, Lightbulb, MapPin, PiggyBank, Shield, Target } from "lucide-react";
import Link from "next/link";
import BaseLayout from "@/components/base-layout";
import { Header } from "@/components/header";

interface Job {
  id: string;
  title: string;
  type: string;
  location: string;
  department: string;
  description: string;
  fullDescription: React.ReactNode;
  responsibilities: string[];
  seniorityLevel: string;
  requirements: string[];
  niceToHave?: string[];
  softRequirements?: string[];
  salaryRange: string;
  compensation: React.ReactNode;
  applyUrl?: string;
}

const JobDescription = ({ children }: { children: React.ReactNode }) => <div className="space-y-3">{children}</div>;

// Helper for compensation section
const Compensation = ({ children }: { children: React.ReactNode }) => (
  <div className="font-bold space-y-4">
    <h4 className="font-semibold">Compensation</h4>
    {children}
  </div>
);

export const jobs: Job[] = [
  {
    id: "full-stack-developer",
    title: "Full-Stack Developer",
    type: "Full-time",
    location: "Amsterdam",
    department: "Product",
    description:
      "Build the backbone of Promptwatch's AI search optimization platform using React 19, Next.js, TypeScript, and Fastify. You'll architect distributed systems processing billions of LLM queries, design real-time APIs with tRPC, manage Postgres databases with Prisma, and deploy on Google Cloud - all while helping businesses dominate ChatGPT, Claude, Perplexity, and Google AI search results.",
    fullDescription: (
      <JobDescription>
        <p>
          Promptwatch is the leading Generative Engine Optimization (GEO) platform, helping businesses optimize their visibility across AI
          assistants like ChatGPT, Claude, Perplexity, and Google AI. We process massive datasets by prompting AI models millions of times
          daily, analyzing website traffic patterns, monitoring server logs, and synthesizing insights that drive real business results for
          SEO and growth teams worldwide.
        </p>

        <p>
          As a Full-Stack Developer, you'll work across our entire technical stack - from PostgreSQL schema design and background job queues
          to React 19 frontends and Fastify APIs. Using technologies like TypeScript, tRPC, Prisma, and Tailwind, you'll build features that
          process billions of AI search queries. Working closely with product and design teams, you'll transform complex AI search data into
          intuitive user experiences that help businesses win in the age of AI-powered search.
        </p>
      </JobDescription>
    ),
    responsibilities: [
      "Owning the entire application layer (React / Next.js, Node / Fastify, data models)",
      "Designing and operating event queues, background jobs, and real-time APIs",
      "Managing Google Cloud CI / CD, container builds, backups, and cost monitoring",
      "Setting up logs, metrics, tracing, dashboards",
      "Pairing with product and design to scope features and run user tests",
      "Driving code reviews + testing culture",
    ],
    seniorityLevel: "Senior (7-8 YOE)",
    requirements: [
      "React 19 and Next.js (pages router)",
      "Strong TypeScript knowledge",
      "Node with Fastify",
      "tRPC + traditional REST endpoints",
      "Event queues and job schedulers (SQS, pgboss, etc.)",
      "Postgres with Prisma or a comparable ORM",
      "Tailwind, Shadcn components",
      "Monorepo tooling (Turborepo, etc.)",
      "Jest or Vitest (or similar)",
    ],
    niceToHave: ["ClickHouse or other OLAP databases", "Elasticsearch", "LLM or prompt-engineering basics", "Advanced data-visualization"],
    softRequirements: ["Clear communication", "Eagerness to learn", "Active in software communities"],
    salaryRange: "€65,000 - €110,000",
    compensation: (
      <Compensation>
        <p>Base salary: €65,000 - €110,000</p>
        <p>Equity: Meaningful equity stake</p>
        <p>Perks: Great office location, team off-sites, company sports activities</p>
        <p>Do you see yourself thriving and growing in this role? Apply now and join us in shaping the future of AI Search.</p>
      </Compensation>
    ),
    applyUrl: "/careers/full-stack-developer",
  },
  {
    id: "design-engineer",
    title: "Design Engineer",
    type: "Full-time",
    location: "Amsterdam",
    department: "Product",
    description:
      "Own the end-to-end product experience from Figma to production UI. You'll design intuitive workflows and build beautiful, performant interfaces in React 19 / Next.js. Help customers understand complex AI search analytics through thoughtful design and robust engineering.",
    fullDescription: (
      <JobDescription>
        <p>
          Promptwatch helps businesses optimize their visibility across AI assistants like ChatGPT, Claude, Perplexity, and Google AI. We
          process large volumes of AI search data and turn them into actionable insights.
        </p>
        <p>
          As a Design Engineer, you'll bridge product design and front-end engineering: translating product problems into elegant UX and
          shipping high-quality, accessible interfaces. You'll move from Figma to TypeScript with ease, building interactive dashboards,
          responsive layouts, and data-rich UI that feels fast and polished.
        </p>
      </JobDescription>
    ),
    responsibilities: [
      "Design and build interactive dashboards, charts, and responsive layouts",
      "Translate Figma designs into production React 19 components using Tailwind / Shadcn",
      "Integrate tRPC endpoints, handle auth states, and manage client state",
      "Optimize Core Web Vitals, performance, and accessibility",
      "Collaborate across Product, Sales, and Marketing; run user tests and iterate",
      "Contribute to our design system and UI quality standards",
    ],
    seniorityLevel: "Medior–Senior (4–7 YOE)",
    requirements: [
      "Figma expert with strong product thinking",
      "React 19 and Next.js (pages router)",
      "Strong TypeScript skills",
      "Tailwind with Shadcn components",
      "tRPC integration experience",
      "Experience building data-heavy applications",
      "Accessibility and UI performance fundamentals",
    ],
    niceToHave: ["Charting libraries (Recharts, ECharts)", "Storybook"],
    softRequirements: ["Product focus", "Eye for design detail", "Collaborative communication", "Good communication skills"],
    salaryRange: "€55,000 – €90,000",
    compensation: (
      <Compensation>
        <p>Base salary: €55,000 – €90,000</p>
        <p>Equity: Meaningful equity stake</p>
        <p>Perks: Great office location, team off-sites, company and team sports activities</p>
        <p>Do you see yourself thriving and growing in this role? Apply now and join us in shaping the future of AI Search.</p>
      </Compensation>
    ),
    applyUrl: "/careers/design-engineer",
  },
  {
    id: "marketing-manager-growth",
    title: "Marketing Manager (Growth)",
    type: "Full-time",
    location: "Amsterdam",
    department: "Growth",
    description:
      "Lead content creation, event planning, and brand building to position Promptwatch as the go-to AI search optimization platform. You'll create case studies and thought leadership, organize webinars and demo sessions, manage partnerships, and work directly with the founding team to execute go-to-market strategy.",
    fullDescription: (
      <JobDescription>
        <p>
          We're looking for a creative and execution-driven Marketing Manager to lead our go-to-market initiatives at Promptwatch. You'll
          work closely with the founding team to shape the way the world sees and understands our brand — from showcasing powerful customer
          use cases to launching new features with impact.
        </p>
        <p>
          This is a hands-on, content-heavy role where brand-building meets execution. Your focus will span storytelling, event
          organization, and growing our organic footprint. If you're someone who loves bringing GTM strategy to life through strong
          narratives and smart distribution, this one's for you.
        </p>
      </JobDescription>
    ),
    responsibilities: [
      "Content creation (case studies, blog posts, thought leadership)",
      "Collaborating with customers to create use cases",
      "Planning and organizing webinars, events, and demo sessions",
      "Managing organic brand exposure and partnerships",
      "Supporting overall go-to-market strategy",
    ],
    seniorityLevel: "Medior–Senior",
    requirements: [
      "Prior experience in B2B SaaS or tech marketing",
      "Strong content creation skills (writing, editing, coordination)",
      "Comfortable working cross-functionally with sales/product",
      "Highly autonomous and self-organizing",
      "Creative thinker with a hands-on attitude",
      "Strong storytelling and brand-building instincts",
      "Excited about AI and the future of search",
    ],
    softRequirements: ["Creativity", "Ownership mindset"],
    salaryRange: "€48,000 – €72,000",
    compensation: (
      <Compensation>
        <p>Salary: €48,000 – €72,000 (depending on experience)</p>
        <p>Equity Compensation: Meaningful equity stake — share in the upside as we scale</p>
        <p>Perks: Great office location, team off-sites, company and team sports activities</p>
        <p>Do you see yourself thriving and growing in this role? Apply now and join us in shaping the future of AI Search.</p>
      </Compensation>
    ),
    applyUrl: "/careers/marketing-manager-growth",
  },
  {
    id: "account-executive",
    title: "Account Executive",
    type: "Full-time",
    location: "Amsterdam",
    department: "Growth",
    description:
      "As an Account Executive at Promptwatch, you'll work closely with the founding team to lay the foundation of our sales strategy. You'll handle inbound leads and close day-to-day deals, while also driving growth through commercial partnerships and outbound initiatives.",
    fullDescription: (
      <JobDescription>
        <p>
          This is a hands-on role where sales ability, commercial creativity, and an ownership mindset come together. You'll collaborate
          directly with the CEO, Customer Success, and Product to help grow Promptwatch into the go-to platform for Generative Engine
          Optimization (GEO)
        </p>
      </JobDescription>
    ),
    responsibilities: [
      "Handling inbound sales leads from demo to close",
      "Managing the entire sales cycle and pipeline",
      "Collaborating with Customer Success to identify upsell opportunities",
      "Supporting the CEO on enterprise and partnership deals",
      "Contributing to outbound sales strategy and execution",
    ],
    seniorityLevel: "Medior",
    requirements: [
      "Proven experience in selling SaaS solutions, ideally in a startup environment",
      "Experience in selling to agencies is a plus",
      "Strong communication and consultative selling skills",
      "Commercially creative and able to think in solutions",
      "Ownership-driven attitude and a proactive mindset",
      "Curious and eager to learn the ins and outs of AI Search",
      "Team player who enjoys working cross-functionally",
    ],
    salaryRange: "€42,000 – €66,000 base + Bonus",
    compensation: (
      <Compensation>
        <p>Salary: €42,000 – €66,000 base + Bonus for hitting sales quota</p>
        <p>Equity Compensation: Meaningful equity stake – be part of our journey as we scale</p>
        <p>Perks: Great office location, team off-sites, company and team sports activities</p>
        <p>Do you see yourself thriving and growing in this role? Apply now and join us in shaping the future of AI Search.</p>
      </Compensation>
    ),
    applyUrl: "/careers/account-executive",
  },
  {
    id: "customer-success-specialist",
    title: "Customer Success Specialist",
    type: "Full-time",
    location: "Amsterdam",
    department: "Growth",
    description:
      "Help users unlock the full value of Promptwatch by leading onboarding, driving adoption, and ensuring customers see our platform as critical to their AI Search strategy. This hands-on role combines product understanding with commercial instinct as you work cross-functionally to turn feedback into growth.",
    fullDescription: (
      <JobDescription>
        <p>
          As the Customer Success Specialist at Promptwatch, you'll play a pivotal role in helping our users unlock the full value of our
          platform. From onboarding to expansion, your mission is to make sure every customer is optimizing their visibility in AI Search
          and sees Promptwatch as a critical part of their digital strategy.
        </p>
        <p>
          This is a hands-on role where product understanding, commercial instinct, and a proactive mindset come together. You'll work
          closely with Sales, Product, and Marketing to turn user feedback into actionable improvements and drive long-term customer growth.
        </p>
      </JobDescription>
    ),
    responsibilities: [
      "Leading customer onboarding and ongoing success",
      "Providing hands-on, timely, and knowledgeable support",
      "Driving product adoption and ensuring users see real value",
      "Acting as the customer voice across Product and Sales",
      "Spotting upsell opportunities and supporting commercial growth",
      "Analyzing user behavior and feedback to improve the platform",
    ],
    seniorityLevel: "Medior",
    requirements: [
      "Proven experience in customer success or software sales (SaaS preferred)",
      "Excellent communication and relationship-building skills",
      "Ability to grasp and explain technical concepts with ease",
      "Proactive and ownership-driven attitude",
      "Startup mindset: hands-on, flexible, and resourceful",
      "Curious and eager to learn the ins and outs of AI Search",
      "Commercial intuition and natural sense for timing",
      "Team player who enjoys collaborating across sales, product, and marketing",
    ],
    salaryRange: "€48,000 – €66,000",
    compensation: (
      <Compensation>
        <p>Salary: €48,000 – €66,000 (depending on experience)</p>
        <p>Equity Compensation: Meaningful equity stake – be part of our journey as we scale</p>
        <p>Perks: Great office location, team off-sites, company and team sports activities</p>
        <p>Do you see yourself thriving and growing in this role? Apply now and join us in shaping the future of AI Search.</p>
      </Compensation>
    ),
    applyUrl: "/careers/customer-success-specialist",
  },
];

const JobCard = ({ job }: { job: Job }) => (
  <Link href={`/careers/${job.id}`}>
    <Card className="group hover:shadow-sm transition-shadow hover:bg-gray-50/80 cursor-pointer">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-2">{job.title}</h3>
            <p className="text-muted-foreground text-sm mb-2 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1">
                <MapPin className="size-4" /> {job.location}
              </span>
              <span className="flex items-center gap-1">
                <Layers className="size-4" /> {job.seniorityLevel}
              </span>
              <span className="flex items-center gap-1">
                <HandCoins className="size-4" /> {job.salaryRange}
              </span>
              <span className="flex items-center gap-1">
                <PiggyBank className="size-4" /> Equity
              </span>
            </p>
            <p className="text-sm mb-1">{job.description}</p>
          </div>
          <div className="flex justify-center md:justify-end">
            <Button variant="outline">
              View Details <ArrowRight className="size-4 ml-2" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const CareersIndex = () => {
  return (
    <BaseLayout>
      <Header
        title="Careers at Promptwatch | Help shape the future of AI search"
        description="Join Promptwatch and help shape the future of AI search optimization. We're building the next generation of AI SEO tools and looking for passionate individuals to join our team."
        metaImage="https://promptwatch.com/meta-careers-image.png"
      />
      <main className="overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />

        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07]">
              <nav aria-label="Breadcrumb" className="p-3">
                <ol className="flex items-center text-sm">
                  <li>
                    <Link href="/" className="text-primary hover:underline">
                      Home
                    </Link>
                  </li>
                  <li className="mx-2 text-muted-foreground">/</li>
                  <li className="text-gray-700" aria-current="page">
                    Careers
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </section>

        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 pb-16 pt-16 sm:px-12">
              <div className="relative w-fit bg-gray-950/5 p-2 mb-6">
                <div aria-hidden="true" className="absolute left-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute right-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 left-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 right-1 size-[3px] rounded-full bg-gray-950/20" />
                <div className="relative flex h-fit items-center gap-2 rounded-full bg-white px-3 py-1 shadow">
                  <span className="text-title text-sm flex items-center gap-2 px-1">Join Our Team</span>
                </div>
              </div>

              <h1 className="text-title mt-6 text-4xl font-medium max-w-3xl sm:mt-12 sm:text-5xl lg:text-5xl">
                Build the Future of AI Search Optimization at{" "}
                <Link href="/" className="text-primary underline">
                  Promptwatch
                </Link>
              </h1>

              <p className="text-lg pt-6 max-w-3xl text-muted-foreground">
                Promptwatch is <span className="font-semibold">one of the largest GEO/AI search optimization platforms</span>, helping
                businesses optimize their presence across <span className="font-semibold">ChatGPT, Claude, Perplexity, Google AI</span> and
                other AI assistants.
              </p>
              <p className="text-lg pt-4 max-w-3xl text-muted-foreground">
                Apply now and join us in shaping the future of <span className="font-semibold">AI Search</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Open Positions Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="relative border-x border-gray-950/[0.07] px-4 py-16 sm:px-12">
              <h2 className="text-2xl font-semibold mb-8">Open Positions ({jobs.length})</h2>

              {Array.from(new Set(jobs.map((j) => j.department))).map((dept) => (
                <div key={dept} className="mb-10">
                  <h3 className="text-xl font-semibold mb-4">{dept}</h3>
                  <div className="space-y-4">
                    {jobs
                      .filter((j) => j.department === dept)
                      .map((job) => (
                        <div key={job.id} className="w-full">
                          <JobCard job={job} />
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="relative border-x border-gray-950/[0.07] px-4 py-16 sm:px-12">
              <div className=" mb-8">
                <h2 className="text-3xl font-semibold">Our Company Values</h2>
                <p className="text-lg pt-3 max-w-3xl text-muted-foreground">
                  We're working on AI search optimization tools that actually help businesses. It's a fast-moving space, so we value getting
                  things done well and quickly while building software we're proud of.
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-start gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <Lightbulb className="size-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Move Fast, Think Smart</h3>
                        <p className="text-muted-foreground text-sm">
                          Things change quickly in our space, so we move fast but always think through what we're doing. We make decisions
                          based on data and get things done without cutting corners.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-start gap-4">
                      <div className="bg-green-50 p-3 rounded-lg">
                        <Target className="size-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Build for Real People</h3>
                        <p className="text-muted-foreground text-sm">
                          We talk to our customers regularly and build features that solve actual problems they're facing. Their success is
                          our success, and we keep it simple.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-start gap-4">
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <Shield className="size-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Care About Quality</h3>
                        <p className="text-muted-foreground text-sm">
                          We write code we can be proud of and build systems that actually work. Good engineering practices aren't optional
                          - they make everything else possible.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-start gap-4">
                      <div className="bg-orange-50 p-3 rounded-lg">
                        <GraduationCap className="size-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Figure Things Out</h3>
                        <p className="text-muted-foreground text-sm">
                          We're working in a pretty new space, so there's a lot of figuring things out as we go. We experiment, learn from
                          what works (and what doesn't), and aren't afraid to try new approaches.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
    </BaseLayout>
  );
};

export default CareersIndex;
